import React, { Fragment, FunctionComponent } from "react";
import { Index, InfiniteLoader } from "react-virtualized";

import { JustifyContent, Layout, SpacingSize } from "@multiweb/reactComponents";
import { asyncDoNothing } from "@multiweb/reactUtils/constants";
import { NotificationItemId } from "@multiweb/types/contracts";
import { LoadingMenuItem } from "../LoadingMenuItem";
import { MenuTitle } from "../MenuTitle";
import { MenuVirtualizedList } from "../MenuVirtualizedList";
import { NoNotificationItems } from "../NoNotificationItems";
import { NotificationItem } from "../NotificationItem";
import { NOTIFICATION_ITEM_HEIGHT } from "../NotificationItemComponent";
import { SetAllNotificationAsReadButton } from "../SetAllNotificationAsReadButton";
import { ShowAllNotificationsButton } from "../ShowAllNotificationsButton";
import { NotificationListComponentProps } from "./NotificationListComponentProps";

export { NotificationListComponentProps };

/** Nastavení cache pro virtualizovaný seznam */
const cellMeasureCacheParams = {
    defaultHeight: NOTIFICATION_ITEM_HEIGHT,
    minHeight: NOTIFICATION_ITEM_HEIGHT,
    fixedWidth: true,
};

/** Komponenta slouží pro vykreslení množiny notifikačních položek menu. */
export const NotificationListComponent: FunctionComponent<NotificationListComponentProps> = ({
    notificationItemIds,
    hasNextPage,
    isNextPageLoading,
    loadNextPage,
    emptyNotificationGroup,
    title,
    focusedItemIndex,
}) => {
    /**
     * Implementace nekonečného načítání inspirovaná oficiálním příkladem.
     * @see https://github.com/bvaughn/react-virtualized/blob/master/docs/creatingAnInfiniteLoadingList.md
     */
    const rowCount = hasNextPage ? notificationItemIds.length + 1 : notificationItemIds.length;

    const asyncLoadNextPage = () => new Promise<void>((resolve) => {
        loadNextPage();
        resolve();
    });

    const loadMoreRows = isNextPageLoading ? asyncDoNothing : asyncLoadNextPage;

    const isRowLoaded = ({ index }: Index) => index < notificationItemIds.length;

    const itemRenderer = (notificationItemId: NotificationItemId, index: number) => {
        if (!isRowLoaded({ index })) {
            return <LoadingMenuItem />;
        }

        return <NotificationItem {...{ notificationItemId }} />;
    };

    const keyGenerator = (notificationItemId: NotificationItemId) => notificationItemId.toString();

    if (emptyNotificationGroup) {
        return (
            <Fragment>
                <MenuTitle text={title} />
                <NoNotificationItems />
            </Fragment>
        );
    }

    // Nulový identifikátor pro načítací indikátor v případě načítání další stránky
    const items = hasNextPage ? [...notificationItemIds, 0] : notificationItemIds;

    return (
        <InfiniteLoader {...{ isRowLoaded, loadMoreRows, rowCount }}>
            {({ onRowsRendered, registerChild }) => (
                <MenuVirtualizedList
                    stickyHeaderContent={<MenuTitle text={title} />}
                    stickyFooterContent={notificationItemIds.length && (
                        <Fragment>
                            <Layout spacingSizeTop={SpacingSize.s} spacingSizeHorizontal={SpacingSize.s} justifyContent={JustifyContent.center}>
                                <ShowAllNotificationsButton />
                            </Layout>
                            <Layout spacingSizeBottom={SpacingSize.s} spacingSizeHorizontal={SpacingSize.s} justifyContent={JustifyContent.center}>
                                <SetAllNotificationAsReadButton />
                            </Layout>
                        </Fragment>
                    )}
                    ref={registerChild}
                    {...{ cellMeasureCacheParams, itemRenderer, keyGenerator, onRowsRendered, focusedItemIndex, items }}
                />
            )}
        </InfiniteLoader>
    );
};
